/**
 * drawing-icons.tsx — Single source-of-truth for all drawing tool icons.
 *
 * Every custom SVG uses a consistent 24×24 viewBox, 1.8 stroke-width design
 * language, and accepts a `className` prop for sizing via Tailwind.
 *
 * Consumers should import `DrawingToolIcon` which takes a `toolId` and renders
 * the correct icon at whatever size you pass via `className`.
 */

import React from "react";
import {
  TrendingUp,
  Minus,
  Square,
  Circle,
  Type,
  MoveRight,
  Magnet,
  Ruler,
  Tag,
} from "lucide-react";

// ── Custom SVG Icons (consistent 24×24 grid) ──

export function VerticalLineIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="3" x2="12" y2="21" strokeWidth="1.8" strokeDasharray="4 2.5" />
      <circle cx="12" cy="3" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="12" cy="21" r="1.2" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function FibRetracementIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="20" x2="21" y2="4" strokeWidth="1.8" />
      <line x1="5" y1="14" x2="19" y2="14" strokeWidth="1" opacity="0.5" strokeDasharray="3 2" />
      <line x1="5" y1="10" x2="19" y2="10" strokeWidth="1" opacity="0.6" strokeDasharray="3 2" />
      <line x1="5" y1="6" x2="19" y2="6" strokeWidth="1" opacity="0.7" strokeDasharray="3 2" />
    </svg>
  );
}

export function PriceLineIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <line x1="2" y1="12" x2="14" y2="12" strokeWidth="1.8" />
      <rect x="14" y="8" width="8" height="8" rx="2" strokeWidth="1.4" fill="currentColor" opacity="0.1" />
      <line x1="16.5" y1="11" x2="19.5" y2="11" strokeWidth="1" opacity="0.6" />
      <line x1="16.5" y1="13" x2="19" y2="13" strokeWidth="1" opacity="0.6" />
    </svg>
  );
}

export function ParallelChannelIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="18" x2="18" y2="6" strokeWidth="1.8" />
      <line x1="6" y1="22" x2="20" y2="10" strokeWidth="1.8" />
      <line x1="4" y1="18" x2="6" y2="22" strokeWidth="1" opacity="0.3" strokeDasharray="2 2" />
      <line x1="18" y1="6" x2="20" y2="10" strokeWidth="1" opacity="0.3" strokeDasharray="2 2" />
    </svg>
  );
}

export function RiskRewardIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      {/* Entry line */}
      <line x1="3" y1="12" x2="21" y2="12" strokeWidth="1.5" strokeDasharray="3 2" />
      {/* Take Profit zone */}
      <rect x="5" y="3.5" width="14" height="5" rx="1.5" strokeWidth="1.3" fill="var(--bullish, #16B85A)" opacity="0.15" />
      <line x1="5" y1="6" x2="19" y2="6" strokeWidth="0.8" opacity="0.4" />
      {/* Stop Loss zone */}
      <rect x="5" y="15.5" width="14" height="5" rx="1.5" strokeWidth="1.3" fill="var(--bearish, #F04545)" opacity="0.15" />
      <line x1="5" y1="18" x2="19" y2="18" strokeWidth="0.8" opacity="0.4" />
    </svg>
  );
}

export function MeasureIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 19L19 5" strokeWidth="1.8" />
      {/* Ruler ticks */}
      <path d="M8 16l1.5-1.5" strokeWidth="1.4" />
      <path d="M11 13l1.5-1.5" strokeWidth="1.4" />
      <path d="M14 10l1.5-1.5" strokeWidth="1.4" />
      {/* End dots */}
      <circle cx="5" cy="19" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="19" cy="5" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function RayIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="17" x2="21" y2="7" strokeWidth="1.8" />
      {/* Arrowhead */}
      <polyline points="17,6 21,7 20,11" strokeWidth="1.5" fill="none" />
      {/* Start dot */}
      <circle cx="3" cy="17" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  );
}

// ── Unified icon registry ──

/**
 * Maps `toolId` (from drawingToolDefinitions) → React component.
 * Every icon is a component that accepts `className` for sizing.
 */
const DRAWING_TOOL_ICON_MAP: Record<string, React.ElementType> = {
  trendLine: TrendingUp,
  horizontalLine: Minus,
  verticalLine: VerticalLineIcon,
  rectangle: Square,
  circle: Circle,
  fibonacci: FibRetracementIcon,
  parallelChannel: ParallelChannelIcon,
  textAnnotation: Type,
  textTag: Tag,
  riskReward: RiskRewardIcon,
  ray: RayIcon,
  priceLine: PriceLineIcon,
  measure: MeasureIcon,
};

/**
 * Maps the `icon` field (string key from drawingToolDefinitions) → React component.
 * This is used by ToolsDrawer which reads `tool.icon`.
 */
const DRAWING_ICON_KEY_MAP: Record<string, React.ElementType> = {
  TrendingUp,
  Minus,
  VerticalLine: VerticalLineIcon,
  Square,
  CircleIcon: Circle,
  Fibonacci: FibRetracementIcon,
  ParallelChannel: ParallelChannelIcon,
  TextAnnotation: Type,
  RiskReward: RiskRewardIcon,
  MoveRight,
  PriceLine: PriceLineIcon,
  Measure: MeasureIcon,
  Tag,
};

/**
 * Public API — render a drawing tool icon by toolId or icon key.
 */
export function DrawingToolIcon({
  toolId,
  iconKey,
  className = "size-4",
}: {
  toolId?: string;
  iconKey?: string;
  className?: string;
}) {
  let Icon: React.ElementType | undefined;

  if (toolId) {
    Icon = DRAWING_TOOL_ICON_MAP[toolId];
  }
  if (!Icon && iconKey) {
    Icon = DRAWING_ICON_KEY_MAP[iconKey];
  }
  if (!Icon) {
    Icon = Square; // fallback
  }

  return <Icon className={className} />;
}

/**
 * Get the icon component for a tool by its ID.
 */
export function getDrawingToolIconComponent(toolId: string): React.ElementType {
  return DRAWING_TOOL_ICON_MAP[toolId] ?? Square;
}

/**
 * Get the icon component for a tool by its icon key string.
 */
export function getDrawingIconByKey(iconKey: string): React.ElementType {
  return DRAWING_ICON_KEY_MAP[iconKey] ?? Square;
}

// Re-export Magnet icon for the snap toggle
export { Magnet as MagnetIcon };
